import { useState, useRef, useCallback, useEffect } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getCopilotBffHeaders, getCopilotBffUrl } from "@/lib/copilot-api";
import { useToast } from "@/hooks/use-toast";
import type { SttProvider } from "@/hooks/use-stt-provider";

interface VoiceButtonProps {
  onTranscript: (text: string) => void;
  sttProvider?: SttProvider;
  disabled?: boolean;
  className?: string;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

// ── Browser Speech Recognition helper ──
const SpeechRecognitionCtor =
  typeof window !== "undefined"
    ? (window as unknown as { SpeechRecognition?: typeof SpeechRecognition; webkitSpeechRecognition?: typeof SpeechRecognition })
        .SpeechRecognition ??
      (window as unknown as { webkitSpeechRecognition?: typeof SpeechRecognition })
        .webkitSpeechRecognition ??
      null
    : null;

export function VoiceButton({
  onTranscript,
  sttProvider = "server",
  disabled,
  className,
}: VoiceButtonProps) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { toast } = useToast();

  // Timer: increment duration every second while recording
  useEffect(() => {
    if (recording) {
      setDuration(0);
      intervalRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setDuration(0);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [recording]);

  // ── Server mode: record WebM → POST to the Agent Runtime through the BFF ──

  const startServerRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunks.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };

      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        await transcribeOnServer(blob);
      };

      mediaRecorder.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      toast({
        title: "Erro no Microfone",
        description: "Não foi possível acessar seu microfone. Verifique as permissões.",
        variant: "destructive",
      });
    }
  }, []);

  const stopServerRecording = useCallback(() => {
    if (mediaRecorder.current && recording) {
      mediaRecorder.current.stop();
      setRecording(false);
    }
  }, [recording]);

  const transcribeOnServer = async (blob: Blob) => {
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append("audio", blob, "recording.webm");

      const res = await fetch(getCopilotBffUrl("stt/transcribe"), {
        method: "POST",
        headers: getCopilotBffHeaders(),
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        if (data.text) {
          onTranscript(data.text);
        } else {
          toast({
            title: "Erro ao transcrever",
            description: "Nenhum texto foi detectado no áudio.",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Erro ao transcrever",
          description: "Erro ao transcrever. Tente novamente.",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Erro ao transcrever",
        description: "Erro ao transcrever. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  // ── Browser mode: Web Speech API (real-time, no backend call) ──

  const startBrowserRecording = useCallback(() => {
    if (!SpeechRecognitionCtor) return;

    try {
      const recognition = new SpeechRecognitionCtor();
      recognition.lang = "pt-BR";
      recognition.continuous = true;
      recognition.interimResults = false;

      let finalText = "";

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalText += (finalText ? " " : "") + event.results[i][0].transcript;
          }
        }
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        // "no-speech" is normal (user didn't speak), don't show error
        if (event.error !== "no-speech") {
          toast({
            title: "Erro no reconhecimento",
            description: "Não foi possível reconhecer a fala. Tente novamente.",
            variant: "destructive",
          });
        }
        setRecording(false);
      };

      recognition.onend = () => {
        if (finalText.trim()) {
          onTranscript(finalText.trim());
        } else {
          toast({
            title: "Nenhum texto detectado",
            description: "Não foi possível detectar fala. Tente novamente.",
            variant: "destructive",
          });
        }
        setRecording(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
      setRecording(true);
    } catch {
      toast({
        title: "Erro no Microfone",
        description: "Não foi possível iniciar o reconhecimento de voz.",
        variant: "destructive",
      });
    }
  }, [onTranscript, toast]);

  const stopBrowserRecording = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      // onend handler will fire and deliver the transcript
    }
  }, []);

  // ── Unified click handler ──

  const handleClick = () => {
    if (recording) {
      if (sttProvider === "browser") {
        stopBrowserRecording();
      } else {
        stopServerRecording();
      }
    } else {
      if (sttProvider === "browser") {
        startBrowserRecording();
      } else {
        startServerRecording();
      }
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={handleClick}
      disabled={disabled || processing}
      className={`relative ${className || ""}`}
      title={recording ? "Parar gravação" : "Gravar áudio"}
      aria-label={recording ? "Parar gravação" : "Gravar áudio"}
    >
      {processing ? (
        <Loader2 className="w-4 h-4 motion-safe:animate-spin text-muted-foreground" />
      ) : recording ? (
        <>
          <MicOff className="w-4 h-4 text-rose-500 shadow-[0_0_12px_rgba(239,68,68,0.4)]" />
          <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-rose-500 rounded-full motion-safe:animate-pulse" />
          <span className="absolute -top-1 -right-7 text-xs text-red-400 tabular-nums font-mono">
            {formatDuration(duration)}
          </span>
        </>
      ) : (
        <Mic className="w-4 h-4 text-muted-foreground" />
      )}
    </Button>
  );
}
